/* eslint-disable no-param-reassign */
import { cloneDeep } from 'lodash';
import {
  getBusinessWifiPriceSelections,
  getBusinessWifiTotals
} from './businessWifi/businessWifi';
import { applyBusinessWifiBundleRate } from './businessWifi/businessWifiBundleRate';

import {
  getCustomerInformationTotals,
  getCustomerInformationPriceSelections
} from './customerInformation/customerInformations';

import {
  getFibreInternetTotals,
  getFibreInternetPriceSelections,
  getFibreInternetConsolidationTotals,
  getFibreInternetConsolidationPriceSelections
} from './fibeInternet/fibreInternet';

import {
  getMobilityTotals,
  getMobilityPriceSelections
} from './mobility/mobility';

import getSummaryConfig, { GET_FIELD_VALUE } from './utils/summaryConfig';
import { getTsb15TakeoverChargesSummaryRow } from './utils/getTsb15TakeoverChargesSummaryRow';
import getFieldSummaryDetailsForOneOption, {
  getFieldSummaryDetailsFromField,
  getSummaryDetailsForFieldWithCouponCode,
  getSummaryDetailsForFieldWithProductCatalogForPriceSummary,
  getSummaryDetailsForFieldWithSelectedOptions
} from './utils/getFieldSummaryDetails';
import {
  getSecureBusinessTotals,
  getSecureBusinessPriceSelections,
  getTsbConsolidationTotals,
  getTsbConsolidationPriceSelections,
  getTsb15AddOnEquipmentCount
} from './secureBusiness/secureBusiness';

import removePriceSubstring from './utils/removePriceSubstring';
import { getLocale } from '../../misc';
import EN from '../../../EN.json';
import FR from '../../../FR.json';
import {
  fieldInstallationFeeAmount,
  keyMonitorAdditionalEquipmentVulcan,
  keyProtectAdditionalEquipmentVulcan,
  preferredLanguageVulcanVariable,
  secureBusinessPlanVulcan,
  smartCameraAdditionalEquipmentVulcan
} from '../../utils/constant';
import {
  getCouponCodePromotion,
  getCouponCodePromotionSelections
} from './couponCodePromotion/couponCodePromotion';

import {
  getBusinessOptikTvTotals,
  getBusinessOptikTvPriceSelections,
  getBusinessOptikTvSummaryDetails
} from './businessOptikTv/businessOptikTv';

import {
  getOfficePhoneLinesPriceSelections,
  getOfficePhoneLinesTotals
} from './officePhone/officePhone';

import {
  getCopperInternetConsolidationPriceSelections,
  getCopperInternetConsolidationTotals
} from './copperInternet/copperInternet';

import {
  getTelusBusinessInternetTotals,
  getTelusBusinessInternetTotalsSelection
} from './telusBusinessInternetBackup/telusBusinessInternetBackup';
import { secureBusinessOffersVulcan } from './secureBusiness/secureBusinessFieldNames';
import { numberOfSelectedItems } from '../utils';
import getFieldValue from '../quoteSummary/productsDetails/getFieldValue';

const getInstallationFeeDiscount = qty => {
  if (qty === 0) return 350;
  if (qty > 0 && qty < 4) return 250;
  if (qty > 3 && qty < 11) return 150;
  return 0;
};

const vulcanPricing = (
  formFields,
  orderData,
  redeemOfferData,
  businessOptikTvTableData,
  officePhoneLinesData,
  fibreInternetData,
  copperInternetData,
  tsbData,
  equipmentBlobs
) => {
  formFields = applyBusinessWifiBundleRate(formFields);
  const { priceSelections = {} } = orderData;
  const totalResults = [
    getCustomerInformationTotals(formFields, priceSelections),
    getFibreInternetTotals(formFields, priceSelections),
    getFibreInternetConsolidationTotals(fibreInternetData),
    getBusinessWifiTotals(formFields, priceSelections),
    getMobilityTotals(formFields, priceSelections),
    getSecureBusinessTotals(
      formFields,
      priceSelections,
      orderData,
      equipmentBlobs
    ),
    getBusinessOptikTvTotals(formFields, businessOptikTvTableData),
    getOfficePhoneLinesTotals(officePhoneLinesData),
    getCouponCodePromotion(redeemOfferData, priceSelections),
    getCopperInternetConsolidationTotals(copperInternetData),
    getTsbConsolidationTotals(tsbData),
    getTelusBusinessInternetTotals(formFields, priceSelections)
  ];
  const total = {
    monthly: 0,
    oneTime: 0,
    threeYearEasyPayment: 0,
    fiveYearEasyPayment: 0
  };
  totalResults.forEach(result => {
    total.oneTime += result.oneTime;
    total.monthly +=
      result.monthly + result.threeYearEasyPayment + result.fiveYearEasyPayment;
  });
  return total;
};

export const getVulcanPriceSelections = (
  formFields,
  redeemOfferData,
  orderData,
  businessOptikTvTableData,
  officePhoneLinesData,
  fibreInternetData,
  copperInternetData,
  tsbData,
  step
) => {
  formFields = applyBusinessWifiBundleRate(formFields);
  let selections = {};
  const priceSelections = [
    getCustomerInformationPriceSelections(formFields),
    getFibreInternetPriceSelections(formFields),
    getFibreInternetConsolidationPriceSelections(fibreInternetData),
    getBusinessWifiPriceSelections(formFields),
    getMobilityPriceSelections(formFields),
    step === 1 ? getSecureBusinessPriceSelections(formFields, orderData) : {},
    getBusinessOptikTvPriceSelections(formFields, businessOptikTvTableData),
    getOfficePhoneLinesPriceSelections(formFields, officePhoneLinesData),
    getCouponCodePromotionSelections(redeemOfferData),
    getCopperInternetConsolidationPriceSelections(copperInternetData),
    getTsbConsolidationPriceSelections(tsbData),
    getTelusBusinessInternetTotalsSelection(formFields)
  ];
  priceSelections.forEach(selection => {
    selections = { ...selections, ...selection };
  });
  return selections;
};

export const getPricingSummary = (
  formFields,
  alternateLanguage,
  redeemOfferData,
  businessOptikTvTableData,
  officePhoneLinesData,
  fibreInternetData,
  copperInternetData,
  tsbData,
  orderData
) => {
  if (!formFields) return [];
  formFields = applyBusinessWifiBundleRate(formFields);

  const productCatalogData = {
    tsbData,
    fibreInternetData,
    copperInternetData
  };

  // Get standard summary sections from config
  const standardSections = getSummaryConfig(alternateLanguage)
    .filter(summaryConfigSectionItem => {
      return (
        summaryConfigSectionItem.shouldShowSection &&
        summaryConfigSectionItem.shouldShowSection(formFields)
      );
    })
    .map(summaryConfigSectionItem => {
      const total = {
        monthlyCharges: 0,
        oneTimeCharges: 0,
        threeYearEasyPaymentCharges: 0,
        fiveYearEasyPaymentCharges: 0
      };
      const processedRows = summaryConfigSectionItem.rows.map(row => {
        let rowDetails = { qty: 0, monthlyCharges: 0, oneTimeCharges: 0 };
        if (row.checkForOption) {
          rowDetails = getFieldSummaryDetailsForOneOption(
            formFields[row.checkForKey],
            row.checkForOption
          );
          const {
            qty,
            monthlyCharges,
            oneTimeCharges,
            threeYearEasyPaymentCharges,
            fiveYearEasyPaymentCharges
          } = rowDetails;
          total.monthlyCharges += monthlyCharges;
          total.oneTimeCharges += oneTimeCharges;
          total.threeYearEasyPaymentCharges += threeYearEasyPaymentCharges;
          total.fiveYearEasyPaymentCharges += fiveYearEasyPaymentCharges;
          return {
            label: row.label,
            description: row.description,
            qty,
            monthlyCharges,
            oneTimeCharges,
            threeYearEasyPaymentCharges,
            fiveYearEasyPaymentCharges
          };
        }
        if (row.checkForKey) {
          const checkForKeyName = Array.isArray(row.checkForKey)
            ? row.checkForKey.find(i => formFields[i] !== 'undefined')
            : row.checkForKey;
          rowDetails = getFieldSummaryDetailsFromField(
            formFields[checkForKeyName]
          );
          const {
            qty,
            description,
            monthlyCharges,
            oneTimeCharges,
            threeYearEasyPaymentCharges,
            fiveYearEasyPaymentCharges
          } = rowDetails;
          let returnObj = {
            label: row.label,
            description:
              row.description === GET_FIELD_VALUE
                ? formFields[row.checkForKey].value
                : row.description || description,
            qty,
            monthlyCharges,
            oneTimeCharges,
            threeYearEasyPaymentCharges,
            fiveYearEasyPaymentCharges
          };
          if (row.transformer) {
            returnObj = row.transformer(returnObj);
          }
          // Function to check if a field should be removed based on substrings
          const shouldRemove = fieldName =>
            row.filterKeySubstringNegativeValues &&
            Array.isArray(row.filterKeySubstringNegativeValues) &&
            row.filterKeySubstringNegativeValues.some(substring =>
              fieldName.includes(substring)
            );

          // List of fields to process
          const fieldsToProcess = [
            { key: 'description', action: 'delete' },
            { key: 'qty', action: 'delete' },
            { key: 'monthlyCharges', action: 'addToTotal' },
            { key: 'oneTimeCharges', action: 'addToTotal' },
            { key: 'threeYearEasyPaymentCharges', action: 'addToTotal' },
            { key: 'fiveYearEasyPaymentCharges', action: 'addToTotal' }
          ];

          // Process each field
          fieldsToProcess.forEach(({ key, action }) => {
            if (shouldRemove(key)) {
              delete returnObj[key];
            } else if (
              action === 'addToTotal' &&
              returnObj[key] !== undefined
            ) {
              total[key] += returnObj[key];
            }
          });
          return returnObj;
        }
        if (row.listSelectedOptionsForKey) {
          getSummaryDetailsForFieldWithSelectedOptions(
            formFields[row.listSelectedOptionsForKey],
            row.addAdditonalInfoIntoValue
              ? row.addAdditonalInfoIntoValue(alternateLanguage, tsbData)
              : null
          ).forEach(summaryItem => {
            total.monthlyCharges += summaryItem.monthlyCharges;
            total.oneTimeCharges += summaryItem.oneTimeCharges;
            total.threeYearEasyPaymentCharges +=
              summaryItem.threeYearEasyPaymentCharges;
            total.fiveYearEasyPaymentCharges +=
              summaryItem.fiveYearEasyPaymentCharges;
          });
          return {
            label: row.label,
            rows: getSummaryDetailsForFieldWithSelectedOptions(
              row.getCustomLabel
                ? row.getCustomLabel(formFields)
                : formFields[row.listSelectedOptionsForKey],
              row.addAdditonalInfoIntoValue
                ? row.addAdditonalInfoIntoValue(alternateLanguage, tsbData)
                : null
            )
          };
        }
        if (
          row.listSelectedValuesForEquipment &&
          formFields[row.listSelectedValuesForEquipment.basedOnKey].value
        ) {
          rowDetails = getFieldSummaryDetailsFromField(
            formFields[row.checkForKey]
          );
          const selectedBaseValue =
            formFields[row.listSelectedValuesForEquipment.basedOnKey].value;
          const requiredEquipmentKeys =
            row.listSelectedValuesForEquipment.map[selectedBaseValue];
          const rows = [];
          requiredEquipmentKeys.forEach(key => {
            // Equipment sections are `custom`/blob-shaped
            // (vulcanContentTypeMap.js), so they are NEVER in `fieldStates`;
            // their data lives only in `engine.values`.
            //
            // On the PAYLOAD path (`getFormData` -> `result.priceSummary`)
            // these rows DO populate as of Stage 4c-iv-a: that call's
            // `formFields` argument is `buildEngineFieldBag(engine)`, which
            // sources the equipment blobs off `engine.values` explicitly.
            // Pinned by getPricingSummary.equipmentGuard.test.js.
            //
            // The `|| {}` still matters for the OTHER caller,
            // pricingAdapter.js, which passes bare `fieldStates` — there the
            // blobs are genuinely absent and this would otherwise throw
            // ("Cannot convert undefined or null to object"). That path's
            // summary output is inert (only `VulcanPricingFooter` reads
            // `pricingResult`, it reads `.selections` not `.summary`, and it
            // only renders in the default-chrome fallback real Vulcan never
            // uses), so the missing rows there have no user-visible effect.
            Object.keys(formFields[key] || {}).forEach(equipmentItem => {
              let shouldCalculate = true;
              const filterKeyBySubstringValue =
                row.listSelectedValuesForEquipment.filterKeysBySubstring;
              const filterKeyBySubstringNegativeValues =
                row.listSelectedValuesForEquipment
                  .filterKeysBySubstringNegative;
              if (filterKeyBySubstringValue) {
                shouldCalculate = [filterKeyBySubstringValue]
                  .flat()
                  .some(substring => {
                    return equipmentItem.includes(substring);
                  });
              }
              if (filterKeyBySubstringNegativeValues) {
                shouldCalculate = !filterKeyBySubstringNegativeValues.some(
                  substring => {
                    return equipmentItem.includes(substring);
                  }
                );
              }
              if (shouldCalculate) {
                rowDetails = getFieldSummaryDetailsFromField(
                  formFields[key][equipmentItem]
                );
                const {
                  sfdcDescriptionKeys
                } = row.listSelectedValuesForEquipment;
                const shouldStoreSfdcDescription =
                  Array.isArray(sfdcDescriptionKeys) &&
                  sfdcDescriptionKeys.some(substring =>
                    equipmentItem.includes(substring)
                  );
                if (+rowDetails.qty)
                  rows.push({
                    ...rowDetails,
                    qty: +rowDetails.qty,
                    description: removePriceSubstring(
                      formFields[key][equipmentItem].fieldLabel || ''
                    ),
                    ...(shouldStoreSfdcDescription
                      ? {
                          sfdcDescription:
                            formFields[key][equipmentItem].fieldLabel || ''
                        }
                      : {}),
                    monthlyCharges:
                      rowDetails.monthlyCharges ||
                      rowDetails.threeYearEasyPaymentCharges ||
                      rowDetails.fiveYearEasyPaymentCharges
                  });
                total.monthlyCharges +=
                  rowDetails.monthlyCharges +
                  rowDetails.threeYearEasyPaymentCharges +
                  rowDetails.fiveYearEasyPaymentCharges;
                total.oneTimeCharges += rowDetails.oneTimeCharges;
                total.threeYearEasyPaymentCharges +=
                  rowDetails.threeYearEasyPaymentCharges;
                total.fiveYearEasyPaymentCharges +=
                  rowDetails.fiveYearEasyPaymentCharges;
              }
            });
          });
          return {
            label: row.label,
            rows
          };
        }
        if (row.tsb15TakeoverCharges) {
          return getTsb15TakeoverChargesSummaryRow(
            formFields,
            row,
            total,
            orderData,
            row.valueForCheck
          );
        }
        if (row.listSelectedOptionsCouponCode) {
          total.monthlyCharges = -redeemOfferData.redeemOfferAdjustedTotalPrice
            .monthlyPrice;
          total.oneTimeCharges = -redeemOfferData.redeemOfferAdjustedTotalPrice
            .oneTimePrice;

          return {
            label: row.label,
            rows: getSummaryDetailsForFieldWithCouponCode(
              row.getCustomLabel
                ? row.getCustomLabel(formFields)
                : redeemOfferData
            )
          };
        }
        if (row.listSelectedOptionsForBusinessOptikTv) {
          const businessOptikTvRows = getBusinessOptikTvSummaryDetails(
            formFields,
            businessOptikTvTableData
          );

          // Calculate totals from the Business Optik TV rows
          businessOptikTvRows.forEach(businessRow => {
            total.monthlyCharges += businessRow.monthlyCharges || 0;
            total.oneTimeCharges += businessRow.oneTimeCharges || 0;
            total.threeYearEasyPaymentCharges +=
              businessRow.threeYearEasyPaymentCharges || 0;
            total.fiveYearEasyPaymentCharges +=
              businessRow.fiveYearEasyPaymentCharges || 0;
          });

          return {
            label: row.label,
            rows: businessOptikTvRows
          };
        }
        if (row.listSelectedOptionsProductCatalog) {
          total.monthlyCharges =
            productCatalogData[
              row.listSelectedOptionsProductCatalog
            ].monthlyCharges;
          total.oneTimeCharges =
            productCatalogData[
              row.listSelectedOptionsProductCatalog
            ].oneTimeCharges;

          return {
            label: row.label,
            rows: getSummaryDetailsForFieldWithProductCatalogForPriceSummary(
              row.getCustomLabel
                ? row.getCustomLabel(formFields)
                : productCatalogData[row.listSelectedOptionsProductCatalog],
              alternateLanguage,
              getLocale()
            )
          };
        }
        return {};
      });
      // Flatten nested rows (like Business Optik TV Table)
      const flattenedRows = [];
      processedRows.forEach(row => {
        if (
          summaryConfigSectionItem.isFlattenedRows &&
          row.rows &&
          Array.isArray(row.rows)
        ) {
          // If the row has nested rows, add them directly to the flattened array
          flattenedRows.push(...row.rows);
        } else {
          // If it's a regular row, add it directly
          flattenedRows.push(row);
        }
      });

      return {
        sectionTitle: summaryConfigSectionItem.sectionTitle,
        rows: flattenedRows,
        total
      };
    });

  // Add Office Phone Lines sections dynamically (one section per phone line)
  const officePhoneSections = getOfficePhoneLinesPriceSelections(
    formFields,
    officePhoneLinesData
  );

  // Combine standard sections with Office Phone sections
  return [...standardSections, ...officePhoneSections];
};

export const getFormFieldSummary = (
  formFields,
  formFieldsForSummary,
  formFieldsCopy,
  orderData
) => {
  const updatedFormFieldSummary = cloneDeep(formFieldsForSummary);
  if (!updatedFormFieldSummary) return {};
  const locale = getLocale();
  const {
    vulMonitorTSB,
    vulProtectTSB,
    vulSmartCameraTSB,
    vulWaivedInstallationFeeOptionTSB
  } = (locale === 'en-US' ? EN : FR).vulcanWebform;
  const additionalEquipmentKeyByPlan = {
    [vulMonitorTSB]: keyMonitorAdditionalEquipmentVulcan,
    [vulProtectTSB]: keyProtectAdditionalEquipmentVulcan,
    [vulSmartCameraTSB]: smartCameraAdditionalEquipmentVulcan
  };
  Object.keys(formFields).forEach(key => {
    if (
      !['MultiCheckbox', 'Radio'].includes(formFields[key].type) &&
      formFields[key].value
    ) {
      if (key === fieldInstallationFeeAmount) {
        updatedFormFieldSummary[key] = {
          ...updatedFormFieldSummary[key],
          value: formFields[key].value,
          monthlyPayment: formFields[key].monthlyPayment,
          oneTimePrice: formFields[key].oneTimePrice
        };
      } else {
        updatedFormFieldSummary[key] = {
          ...updatedFormFieldSummary[key],
          value: formFields[key].value
        };
      }
    } else if (
      ['MultiCheckbox', 'Radio'].includes(formFields[key].type) &&
      formFields[key].value &&
      formFieldsCopy[key] &&
      updatedFormFieldSummary[key]
    ) {
      // `formFields` (engine.fieldStates) spans every step; `formFieldsCopy`/
      // `formFieldsForSummary` are still per-step (populated once by
      // normalizedFields for whichever step is currently rendered) — a field
      // belonging to a DIFFERENT step (echoed back into fieldStates from
      // orderData after Back navigation) has no entry here. Nothing to
      // summarize for it this render; skip rather than crash reading
      // `.options` off undefined (confirmed real-data bug: step1 -> step4 ->
      // Back -> Back -> Back -> Continue from step1).
      //
      // The alternate-language (summary) offer options are cloned straight from
      // Contentful and are NOT transformed by handleInstallationFeeDiscount,
      // unlike the current-locale options (formFieldsCopy) which have the waived
      // installation-fee option removed and — when eligible — a percentage
      // discount option appended. That divergence breaks the positional index
      // mapping below and mislabels the percentage discount as a different offer
      // (e.g. TSBPROTECT5OFF60) in the French price summary. Mirror the current
      // locale's transformation onto the alternate-language options here so the
      // two option arrays stay index-aligned.
      if (
        formFields[key].name === secureBusinessOffersVulcan &&
        Array.isArray(formFieldsCopy[key].options)
      ) {
        const currentStrings = (locale === 'en-US' ? EN : FR).vulcanWebform;
        const alternateStrings = (locale === 'en-US' ? FR : EN).vulcanWebform;
        const currentWaivedValue =
          currentStrings.vulWaivedInstallationFeeOptionTSB;
        const currentPercentageValue =
          currentStrings.vulDiscountForInstallationFeeOptionTSB;
        const alternateWaivedValue =
          alternateStrings.vulWaivedInstallationFeeOptionTSB;
        const alternatePercentageValue =
          alternateStrings.vulDiscountForInstallationFeeOptionTSB;
        const currentHasWaivedOption = formFieldsCopy[key].options.some(
          option => option.fields.value === currentWaivedValue
        );
        const currentHasPercentageOption = formFieldsCopy[key].options.some(
          option => option.fields.value === currentPercentageValue
        );
        [formFieldsForSummary[key], updatedFormFieldSummary[key]].forEach(
          fieldGroup => {
            if (!fieldGroup || !Array.isArray(fieldGroup.options)) return;
            // Mirror waived-option removal.
            if (!currentHasWaivedOption) {
              const waivedIndex = fieldGroup.options.findIndex(
                option => option.fields.value === alternateWaivedValue
              );
              if (waivedIndex > -1) fieldGroup.options.splice(waivedIndex, 1);
            }
            // Mirror percentage-option presence (always appended last, as the
            // current locale does).
            const percentageIndex = fieldGroup.options.findIndex(
              option => option.fields.value === alternatePercentageValue
            );
            if (currentHasPercentageOption) {
              if (percentageIndex === -1) {
                fieldGroup.options.push({
                  fields: {
                    label: alternatePercentageValue,
                    value: alternatePercentageValue
                  }
                });
              }
            } else if (percentageIndex > -1) {
              fieldGroup.options.splice(percentageIndex, 1);
            }
          }
        );
      }
      formFieldsCopy[key].options.forEach((field, index) => {
        if (Array.isArray(formFields[key].value)) {
          if (!Array.isArray(updatedFormFieldSummary[key].value)) {
            updatedFormFieldSummary[key].value = [];
          }
          if (formFields[key].value.includes(field.fields.value)) {
            if (formFields[key].name === secureBusinessOffersVulcan) {
              const planField = getFieldValue(
                secureBusinessPlanVulcan,
                formFields
              );
              const planValue = planField && planField.value;
              const equipmentKey = additionalEquipmentKeyByPlan[planValue];
              let numberOfSelectedAdditionalEquipments = equipmentKey
                ? numberOfSelectedItems(formFields[equipmentKey])
                : 0;
              numberOfSelectedAdditionalEquipments += getTsb15AddOnEquipmentCount(
                formFields,
                orderData
              );

              const optionFields = formFieldsCopy[key].options[index].fields;
              const optionFieldsAlternative =
                updatedFormFieldSummary[key].options[index].fields;
              const hasWaivedInstallation =
                optionFields.value === vulWaivedInstallationFeeOptionTSB;

              if (hasWaivedInstallation) {
                optionFieldsAlternative.oneTimePrice = -getInstallationFeeDiscount(
                  numberOfSelectedAdditionalEquipments
                );
              } else {
                if (field.fields.monthlyPayment)
                  optionFieldsAlternative.monthlyPayment =
                    field.fields.monthlyPayment;
                if (field.fields.oneTimePrice)
                  optionFieldsAlternative.oneTimePrice =
                    field.fields.oneTimePrice;
              }
            }
            updatedFormFieldSummary[key] = {
              ...updatedFormFieldSummary[key],
              value: [
                ...updatedFormFieldSummary[key].value,
                formFieldsForSummary[key].options[index].fields.value
              ]
            };
          }
        } else if (formFields[key].value === field.fields.value) {
          // This condition ensures that the logic to update `monthlyPayment` is only applied
          // to the field with the name 'secureBusinessPlanVulcan'. Without this check,
          // the logic would run for all radio fields, which could lead to unintended updates
          if (formFields[key].name === 'secureBusinessPlanVulcan') {
            // `formFields[key].options` (the LIVE, fieldRules-transformed
            // list) can be a DIFFERENT length than
            // `updatedFormFieldSummary[key].options` (a static per-locale
            // snapshot, never touched by fieldRules) — e.g. the plan rule
            // filters out Smart Camera / existing-equipment-excluded values.
            // Matching by index crashed ("Cannot read properties of
            // undefined (reading 'fields')") once the live list got shorter;
            // match by option VALUE instead, and skip an option the live
            // list no longer carries. The alternate-language values differ
            // from the live (current-locale) ones, so resolve each through
            // its index-aligned `formFieldsCopy` counterpart first —
            // otherwise nothing matches and the $10 bundle discount baked
            // into the live monthlyPayment never reaches the other-language
            // quote.
            updatedFormFieldSummary[key].options.forEach((f, optionIndex) => {
              const counterpart = formFieldsCopy[key].options[optionIndex];
              const liveValue = counterpart
                ? counterpart.fields.value
                : f.fields.value;
              const liveOption = formFields[key].options.find(
                o => o.fields.value === liveValue
              );
              if (liveOption)
                f.fields.monthlyPayment = liveOption.fields.monthlyPayment;
            });
          }
          if (updatedFormFieldSummary[key]) {
            updatedFormFieldSummary[key] = {
              ...updatedFormFieldSummary[key],
              value: formFieldsForSummary[key].options[index].fields.value
            };
          }
        }
      });
    } else if (!formFields[key].type && updatedFormFieldSummary[key]) {
      // Same cross-step gap as above, for blob-shaped (equipment) keys —
      // `updatedFormFieldSummary[key]` only exists for the currently
      // rendered step's own fields.
      Object.keys(formFields[key]).forEach(field => {
        if (formFields[key][field].value) {
          updatedFormFieldSummary[key][field] = {
            ...updatedFormFieldSummary[key][field],
            value: formFields[key][field].value,
            threeYearEasyPayment: formFields[key][field].threeYearEasyPayment,
            fiveYearEasyPayment: formFields[key][field].fiveYearEasyPayment,
            monthlyPayment: formFields[key][field].monthlyPayment,
            oneTimePrice: formFields[key][field].oneTimePrice
          };
        }
      });
    }
  });
  return updatedFormFieldSummary;
};

export const getLocalLanguageIsDifferent = formFields => {
  const locale = getLocale();
  if (formFields.preferredLanguageVulcan) {
    if (
      preferredLanguageVulcanVariable[locale].includes(
        formFields.preferredLanguageVulcan.value
      )
    ) {
      return false;
    }
  }
  return true;
};

export default vulcanPricing;
